(() => {
  'use strict';
  const MAX_IMAGES = 30;
  const HELD_SKUS = new Set(['CR-G321K006-W', 'XWH-62736HNM', 'K22-M13CW-BKV3E61']);
  const TRIGGERS = 'details[data-odp-group] > summary, li[data-odp-child] > a';
  const safeCategory = (id, value) => /^202601:\d+$/.test(id) && value === `/collections/odp-${id.replace(':', '-')}` ? value : null;
  const safeImage = (value) => {
    try { const u = new URL(value); return u.protocol === 'https:' && u.hostname === 'cdn.shopify.com' && u.pathname.startsWith('/s/files/') && !u.hash && !u.username && !u.password ? u.href : null; } catch (_) { return null; }
  };
  function selectNode(data, nodeId) {
    const raw = data?.nodes?.[nodeId], categoryUrl = safeCategory(nodeId, raw?.categoryUrl);
    if (!categoryUrl || typeof raw.title !== 'string') return null;
    const seen = new Set(), items = [];
    for (const sku of Array.isArray(raw.itemIds) ? raw.itemIds : []) {
      const item = data.items?.[sku];
      if (!item || item.sku !== sku || sku.startsWith('K23-') || HELD_SKUS.has(sku) || !item.nodeIds?.includes(nodeId) || !item.title || !/^[a-f0-9]{64}$/.test(item.imageHash) || seen.has(item.imageHash) || !safeImage(item.imageUrl)) continue;
      seen.add(item.imageHash); items.push({ ...item, categoryNodeId: nodeId, categoryUrl });
      if (items.length === MAX_IMAGES) break;
    }
    return { nodeId, title: raw.title, categoryUrl, items, imageCount: items.length };
  }
  function destination(item, resolver, origin) {
    const fallback = safeCategory(item.categoryNodeId, item.categoryUrl);
    if (resolver?.resolve) {
      try {
        // No staged handle is supplied. The shared resolver must prove an exact
        // public SKU/variant from its current catalog before returning a product.
        const result = resolver.resolve(item, { origin });
        if (result?.kind === 'product') {
          const u = new URL(result.href, origin), variant = u.searchParams.get('variant');
          if (u.origin === origin && /^\/products\/[\p{L}\p{N}%_-]+$/u.test(u.pathname) && /^[1-9]\d*$/.test(variant || '') && u.searchParams.getAll('variant').length === 1 && [...u.searchParams.keys()].every(k => k === 'variant') && !u.hash && !u.username && !u.password) return { href: u.pathname + u.search, kind: 'product' };
        }
      } catch (_) { /* Exact category remains available during resolver startup. */ }
    }
    return { href: fallback, kind: 'category' };
  }
  const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));
  function layout(trigger, viewport, headerBottom = 0, desiredHeight = 540, compact = false) {
    const margin = 12, gap = 14, topEdge = Math.max(margin, headerBottom + 10);
    const widthLimit = Math.max(1, viewport.width - margin * 2);
    const right = viewport.width - margin - trigger.right - gap, left = trigger.left - margin - gap;
    const room = Math.max(right, left);
    let side = right >= left ? 'right' : 'left';
    let width = Math.min(408, room);
    if (compact || width < 250) { side = 'compact'; width = Math.min(408, widthLimit); }
    width = Math.min(width, widthLimit);
    const height = Math.min(desiredHeight, Math.max(1, viewport.height - topEdge - margin));
    const x = side === 'right' ? trigger.right + gap : side === 'left' ? trigger.left - gap - width : (viewport.width - width) / 2;
    const y = side === 'compact' ? Math.max(topEdge, trigger.bottom + 8) : trigger.top - 16;
    return { side, left: clamp(x, margin, viewport.width - width - margin), top: clamp(y, topEdge, viewport.height - height - margin), width, height, headerBottom: topEdge };
  }
  const inRect = (point, rect, pad = 0) => point.x >= rect.left - pad && point.x <= rect.right + pad && point.y >= rect.top - pad && point.y <= rect.bottom + pad;
  function inEnvelope(point, trigger, panel) {
    if (!point || !trigger || !panel) return false;
    if (inRect(point, trigger, 6) || inRect(point, panel, 8)) return true;
    let a, b;
    if (panel.left >= trigger.right) { a = { x: trigger.right, top: trigger.top - 6, bottom: trigger.bottom + 6 }; b = { x: panel.left, top: panel.top - 8, bottom: panel.bottom + 8 }; }
    else if (trigger.left >= panel.right) { a = { x: panel.right, top: panel.top - 8, bottom: panel.bottom + 8 }; b = { x: trigger.left, top: trigger.top - 6, bottom: trigger.bottom + 6 }; }
    else return false;
    if (point.x < a.x || point.x > b.x) return false;
    const t = (point.x - a.x) / Math.max(1, b.x - a.x);
    return point.y >= a.top + (b.top - a.top) * t && point.y <= a.bottom + (b.bottom - a.bottom) * t;
  }
  function stickyBottom(rects) {
    let edge = 0;
    const pending = rects.filter(r => r.bottom > 0 && r.width > 0 && r.height > 0).sort((a,b) => a.top - b.top);
    for (const rect of pending) if (rect.top <= edge + 24) edge = Math.max(edge, rect.bottom);
    return edge;
  }
  const api = { selectNode, destination, layout, inEnvelope, stickyBottom, TRIGGERS };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('ace-classification-preview')) return;

  class AceClassificationPreview extends HTMLElement {
    async connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); const { signal } = this.abort;
      this.browser = document.getElementById(this.dataset.browserId);
      if (!this.browser?.matches('ace-odp-category-browser')) return;
      this.triggerRecords = new Map(); this.affordances = [];
      this.panel = document.createElement('section');
      this.panel.className = 'ace-classification-preview__panel';
      this.panel.id = `${this.dataset.browserId}-image-preview`;
      this.panel.hidden = true; this.panel.setAttribute('role', 'region');
      this.panel.setAttribute('aria-label', '分類の商品画像'); this.panel.setAttribute('tabindex', '-1');
      document.body.append(this.panel);
      this.listen(this.browser, 'pointerover', e => {
        if (e.pointerType === 'touch') return;
        const record = this.recordFor(e.target);
        if (!record || record.trigger.contains(e.relatedTarget) || record === this.current) return;
        this.keyboardMode = false;
        clearTimeout(this.openTimer); this.openTimer = setTimeout(() => this.open(record, false), 90);
      });
      this.listen(this.browser, 'pointerout', e => {
        const record = this.recordFor(e.target);
        if (Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) this.pointer = { x: e.clientX, y: e.clientY };
        if (record && !record.trigger.contains(e.relatedTarget)) { clearTimeout(this.openTimer); this.scheduleClose(); }
      });
      this.listen(this.browser, 'pointerdown', e => { this.lastPointer = e.pointerType; this.keyboardMode = false; });
      this.listen(this.browser, 'focusin', e => {
        const record = this.recordFor(e.target);
        if (record && e.target !== record.button && this.lastPointer !== 'touch') { this.keyboardMode = !this.lastPointer; this.open(record, false); }
        this.lastPointer = null;
      });
      this.listen(this.browser, 'keydown', e => {
        const record = this.recordFor(e.target);
        if (record && e.key === 'ArrowRight') { e.preventDefault(); this.keyboardMode = true; this.open(record, true); }
      });
      this.listen(document, 'keydown', e => {
        if (e.key === 'Escape' && this.current) { e.preventDefault(); this.close(true); }
      });
      this.listen(document, 'pointermove', e => {
        if (!this.current || e.pointerType === 'touch') return;
        this.pointer = { x: e.clientX, y: e.clientY };
        if (this.pointerWithin()) { clearTimeout(this.closeTimer); this.closeTimer = null; } else this.scheduleClose();
      });
      this.listen(document, 'pointerdown', e => {
        if (this.current && !this.panel.contains(e.target) && !this.current.trigger.contains(e.target) && !this.current.button.contains(e.target)) this.close(false);
      }, true);
      this.listen(document, 'focusin', e => {
        if (this.current && !this.panel.contains(e.target) && !this.current.trigger.contains(e.target) && e.target !== this.current.button) this.scheduleClose();
      });
      this.listen(this.panel, 'pointerenter', () => { clearTimeout(this.closeTimer); this.closeTimer = null; });
      this.listen(this.panel, 'pointerleave', () => this.scheduleClose());
      this.listen(this.panel, 'focusout', () => this.scheduleClose());
      this.listen(document, 'ace:product-destination-ready', () => this.refreshLinks());
      this.listen(document, 'ace:board-ready', () => this.refreshLinks());
      this.listen(document, 'ace:catalog-items', () => this.refreshLinks());
      this.listen(window, 'blur', () => this.close(false));
      this.listen(document, 'pointerout', e => { if (!e.relatedTarget) { this.pointer = null; this.scheduleClose(); } });
      this.listen(window, 'resize', () => this.requestPosition());
      this.listen(window, 'scroll', () => this.requestPosition(), true);
      this.listen(window.visualViewport, 'resize', () => this.requestPosition());
      this.listen(window.visualViewport, 'scroll', () => this.requestPosition());
      this.observer = new MutationObserver(() => { this.bindTriggers(); if (this.current && !this.isVisible(this.current.trigger)) this.close(false); });
      this.observer.observe(this.browser, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open'] });
      try {
        const response = await fetch(this.dataset.manifestUrl, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Preview data unavailable');
        const data = await response.json();
        if (signal.aborted) return;
        this.data = data; this.bindTriggers();
        const focused = this.recordFor(document.activeElement);
        const hovered = matchMedia('(any-hover: hover)').matches && [...this.triggerRecords.values()].find(record => record.trigger.matches(':hover'));
        if (focused && document.activeElement !== focused.button && this.lastPointer !== 'touch') { this.keyboardMode = true; this.open(focused, false); }
        else if (hovered && this.lastPointer !== 'touch') this.open(hovered, false);
        const resolver = window.AceProductDestination;
        if (resolver?.ready) Promise.resolve(resolver.ready).then(() => { if (!signal.aborted) this.refreshLinks(); }, () => {});
      } catch (_) { /* Category links and native details continue without previews. */ }
    }
    listen(target, type, fn, capture = false) { target?.addEventListener(type, fn, { signal: this.abort.signal, capture }); }
    bindTriggers() {
      if (!this.data) return;
      for (const trigger of this.browser.querySelectorAll(TRIGGERS)) {
        if (this.triggerRecords.has(trigger)) continue;
        const nodeId = trigger.parentElement.getAttribute(trigger.tagName === 'SUMMARY' ? 'data-odp-group' : 'data-odp-child');
        const node = selectNode(this.data, nodeId); if (!node) continue;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'ace-classification-preview__toggle';
        button.textContent = '画像'; button.setAttribute('aria-label', `${node.title}の商品画像を見る`);
        button.setAttribute('aria-controls', this.panel.id); button.setAttribute('aria-expanded', 'false');
        const record = { trigger, button, nodeId, node };
        this.triggerRecords.set(trigger, record); this.affordances.push(button);
        // A separate button never changes the original category anchor's action.
        if (trigger.tagName === 'SUMMARY') trigger.append(button); else trigger.insertAdjacentElement('afterend', button);
        this.listen(button, 'click', e => {
          e.preventDefault(); e.stopPropagation();
          if (this.current === record && this.returnFocus === record.button && !this.panel.hidden) this.close(true); else this.open(record, true);
        });
        this.listen(button, 'keydown', e => { if (e.key === ' ' || e.key === 'Enter') e.stopPropagation(); });
      }
    }
    recordFor(target) {
      if (!target?.closest) return null;
      const button = target.closest('.ace-classification-preview__toggle');
      if (button) return [...this.triggerRecords.values()].find(r => r.button === button) || null;
      const trigger = target.closest(TRIGGERS);
      return this.triggerRecords.get(trigger) || null;
    }
    isVisible(trigger) {
      if (!trigger.isConnected || trigger.closest('[hidden]')) return false;
      for (let parent = trigger.parentElement; parent && parent !== this.browser; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS' && !parent.open && trigger !== parent.querySelector(':scope > summary')) return false;
      }
      return trigger.getBoundingClientRect().width > 0;
    }
    open(record, focusPanel) {
      clearTimeout(this.openTimer); clearTimeout(this.closeTimer); this.closeTimer = null;
      if (!this.isVisible(record.trigger)) return;
      if (this.current !== record) {
        if (this.current) this.current.button.setAttribute('aria-expanded', 'false');
        this.returnFocus = null; this.current = record; this.render(record.node);
      }
      record.button.setAttribute('aria-expanded', 'true'); this.panel.hidden = false;
      this.position();
      if (focusPanel) { this.returnFocus = record.button; this.panel.querySelector('a')?.focus({ preventScroll: true }); }
    }
    render(node) {
      const el = (tag, cls, text) => { const element = document.createElement(tag); if (cls) element.className = cls; if (text) element.textContent = text; return element; };
      const heading = el('div', 'ace-classification-preview__heading');
      const title = el('h3', '', node.title); title.id = `${this.panel.id}-title`;
      this.panel.setAttribute('aria-labelledby', title.id);
      heading.append(title);
      const subtitle = el('p', 'ace-classification-preview__note', node.items.length ? `${node.items.length}点の商品画像` : 'この分類の商品画像はまだありません');
      const scroll = el('div', 'ace-classification-preview__scroll');
      const list = el('ul', 'ace-classification-preview__grid'); list.setAttribute('role', 'list');
      this.imageLinks = [];
      for (const item of node.items) {
        const li = el('li'), a = el('a', 'ace-classification-preview__tile');
        a.setAttribute('data-ace-destination-sku', item.sku);
        if (/^[1-9]\d*$/.test(item.productId || '') && /^[1-9]\d*$/.test(item.variantId || '')) a.setAttribute('data-ace-destination-key', `${item.productId}.${item.variantId}`);
        this.listen(a, 'click', () => this.refreshLinks(), true);
        const img = el('img'); img.src = item.imageUrl; img.alt = item.title; img.loading = 'lazy'; img.decoding = 'async'; img.width = 240; img.height = 180;
        a.append(img, el('span', 'ace-classification-preview__caption', item.title));
        if (item.inquiryOnly) a.append(el('span', 'ace-classification-preview__badge', '構成確認・要問い合わせ'));
        const cue = el('span', 'ace-classification-preview__link-note'); a.append(cue);
        this.imageLinks.push({ a, cue, item }); li.append(a); list.append(li);
      }
      scroll.append(list);
      const browse = el('a', 'ace-classification-preview__browse', 'この分類の商品を見る →'); browse.href = node.categoryUrl;
      const hint = el('p', 'ace-classification-preview__hint', '写真を選ぶと商品または分類ページに進みます。');
      this.panel.replaceChildren(heading, subtitle, scroll, browse, hint);
      // The shared v2 observer lazily hydrates these exact-ID/SKU anchors. Do
      // not pass preview rows as its catalog; that would erase public records.
      window.AceProductDestination?.bind?.(this.panel);
      this.refreshLinks();
    }
    refreshLinks() {
      for (const { a, cue, item } of this.imageLinks || []) {
        const result = destination(item, window.AceProductDestination, location.origin);
        a.href = result.href; cue.textContent = result.kind === 'product' ? '商品を見る →' : '分類から探す →';
        a.setAttribute('data-preview-destination', result.kind);
      }
    }
    headerBottom() {
      const selectors = 'header, [role="banner"], sticky-header, header-component, [id*="header"], [class*="header"], [class*="sticky"], [class*="announcement"], [class*="trust-strip"], [class*="category-menu"]';
      const rects = [];
      for (const element of document.querySelectorAll(selectors)) {
        if (this.panel.contains(element) || this.browser.contains(element)) continue;
        const style = getComputedStyle(element);
        if (style.position !== 'fixed' && style.position !== 'sticky') continue;
        const r = element.getBoundingClientRect();
        if (style.visibility !== 'hidden' && style.display !== 'none' && r.top < innerHeight / 2) rects.push(r);
      }
      return stickyBottom(rects);
    }
    requestPosition() {
      if (!this.current || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; if (this.current) this.position(); });
    }
    position() {
      if (!this.current || !this.isVisible(this.current.trigger)) { if (this.current) this.close(false); return; }
      const viewport = { width: document.documentElement.clientWidth || innerWidth, height: window.visualViewport?.height || innerHeight };
      const rect = this.current.trigger.getBoundingClientRect();
      const header = this.headerBottom();
      if (rect.bottom < header || rect.top > viewport.height) { this.close(false); return; }
      const compact = matchMedia('(max-width: 700px)').matches;
      const height = this.current.node.items.length ? Math.min(560, 188 + Math.ceil(this.current.node.items.length / 2) * 164) : 220;
      const result = layout(rect, viewport, header, height, compact);
      this.panel.dataset.side = result.side;
      this.panel.style.left = `${result.left}px`; this.panel.style.top = `${result.top}px`;
      this.panel.style.width = `${result.width}px`; this.panel.style.maxHeight = `${result.height}px`;
    }
    pointerWithin() { return this.current && inEnvelope(this.pointer, this.current.trigger.getBoundingClientRect(), this.panel.getBoundingClientRect()); }
    scheduleClose() {
      if (!this.current || this.closeTimer) return;
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        const focus = document.activeElement;
        if (!this.current || this.panel.contains(focus) || (this.keyboardMode && (this.current.trigger.contains(focus) || focus === this.current.button)) || this.pointerWithin()) return;
        this.close(false);
      }, 240);
    }
    close(restoreFocus) {
      clearTimeout(this.openTimer); clearTimeout(this.closeTimer); this.closeTimer = null; this.openTimer = this.closeTimer = null;
      if (!this.current) return;
      const record = this.current, target = this.returnFocus || record.trigger;
      this.current = null; this.pointer = null; this.returnFocus = null;
      record.button.setAttribute('aria-expanded', 'false'); this.panel.hidden = true;
      if (restoreFocus && target.isConnected) {
        // Restoring focus must not reopen the panel through focusin.
        this.lastPointer = 'touch'; target.focus({ preventScroll: true }); this.lastPointer = null;
      }
    }
    disconnectedCallback() {
      this.close(false); this.abort?.abort(); this.abort = null; this.observer?.disconnect();
      cancelAnimationFrame(this.frame); this.frame = null;
      this.affordances?.forEach(button => button.remove()); this.panel?.remove();
    }
  }
  customElements.define('ace-classification-preview', AceClassificationPreview);
})();
